<script setup lang="ts">
import { watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useDisplay } from 'vuetify';

import ActionButton from '@/components/buttons/ActionButton.vue';

import UpdateAvatar from '@/features/avatar/UpdateAvatar.vue';
import UpdateCover from '@/features/cover/UpdateCover.vue';
import { useMediaStore } from '@/features/overlays/modals/media/mediaStore';

import useMeQuery from '@/api/queries/me/useMeQuery';
import { useLocaleStore } from '@/stores/locales';
import { useUserDataMutation } from '@/api/mutations/user/use-user-data.mutation';
import { useCvStore } from '../cv-upload/cvUploadStore';
import { useModalStore } from '../modalStore';
import { useAvatarStore } from '@/features/avatar/avatarStore';

const { auth } = useMeQuery();

const mediaStore = useMediaStore();
const modalStore = useModalStore();
const avatarStore = useAvatarStore();

const { isOpen, section, showOnlyAvatarModal } = storeToRefs(mediaStore);
const { mutateAsync: updateUserData } = useUserDataMutation();

const { smAndDown } = useDisplay();

// tmp fix, to load namespace
await Promise.all([useLocaleStore().loadNamespaces('profile')]);

function handleCloseModal() {
  if (showOnlyAvatarModal.value) {
    // Patch candidate_first_connection to false
    updateUserData({ tutorial: { candidate_first_connection: false } });
    setTimeout(() => {
      showOnlyAvatarModal.value = false;
    }, 500); // to avoid modal glitch during close transition
  }
  mediaStore.closeModal();
}

watch(
  () => [modalStore.invitationCompanySharepool.visible, modalStore.cguUpdate.visible],
  ([isInvitationVisible, shouldOpenCgu]) => {
    if (
      !isInvitationVisible &&
      !shouldOpenCgu &&
      auth.value?.user_data?.tutorial?.candidate_first_connection &&
      useCvStore().alreadyUpload
    ) {
      if (avatarStore.hasAvatar) {
        // Avatar already set: skip the modal and end the tutorial ourselves
        updateUserData({ tutorial: { candidate_first_connection: false } });
        return;
      }
      showOnlyAvatarModal.value = true;
      mediaStore.openModal();
    }
  },
  { immediate: true },
);
</script>

<template>
  <v-dialog
    max-width="600px"
    v-model="isOpen"
    :fullscreen="smAndDown"
    @click:outside="handleCloseModal"
  >
    <v-card class="card">
      <v-tabs v-if="!showOnlyAvatarModal" v-model="section" grow stacked slider-color="primary">
        <v-tab value="avatar" class="px-4" :tabindex="0">
          <div class="flex gap-2 items-center">
            <v-icon icon="fa-thin fa-user" :size="14"></v-icon>
            <p>
              {{
                smAndDown
                  ? $t('candidate_profile.picture.short')
                  : $t('profile.photo.profile.tab_title')
              }}
            </p>
          </div>
        </v-tab>

        <v-tab value="cover" class="px-4" :tabindex="0">
          <div class="flex gap-2 items-center">
            <v-icon icon="fa-thin fa-image" :size="14"></v-icon>
            <p>
              {{
                smAndDown
                  ? $t('candidate_profile.cover.short')
                  : $t('profile.photo.banner.tab_title')
              }}
            </p>
          </div>
        </v-tab>
      </v-tabs>
      <v-card-text class="px-0 py-0">
        <v-window :touch="false" v-model="section">
          <v-window-item value="avatar">
            <UpdateAvatar />
          </v-window-item>
          <v-window-item value="cover">
            <UpdateCover />
          </v-window-item>
        </v-window>
      </v-card-text>

      <v-card-actions class="d-flex flex-column">
        <div class="d-flex justify-center gap-4 w-100 pa-4">
          <ActionButton cancel @click="handleCloseModal">
            {{ $t('general.close') }}
          </ActionButton>
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped lang="scss">
.cover {
  &_default {
    display: flex;
    flex-wrap: wrap;
    &-imgs {
      width: calc(50% - 4px);
      margin: 2px;
    }
  }
}

.v-dialog > .v-overlay__content > .v-card {
  border-radius: 10px;
}

.v-tabs--density-default.v-tabs--stacked,
.v-tab.v-tab.v-btn {
  height: 58px;
}

.card {
  display: grid !important;
}
</style>
